import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { PromoCode, PromoCodeStatus } from 'models/PromoCode';
import FormattedDateCell from 'components/table/cells/common/FormattedDateCell';
import YesOrNoCell from 'components/table/cells/common/YesOrNo';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import useFetchAllPropertiesForPromoCodes from '../useFetchAllPropertiesForPromoCodes';
import { PromoCodesTableStyled } from './PromoCodesTable.styles';

import AmountPercentageCell from './cells/AmountPercentageCell';
import PropertyNameCell from './cells/PropertyNameCell';
import ValidDatesCell from './cells/ValidDatesCell';
import ActionsCell from './cells/ActionsCell';

import { promoCodesQuery } from './PromoCodesTable.utils';

const PromoCodeTable = ({ status }: { status: PromoCodeStatus }) => {
  const { isPropertiesLoading } = useFetchAllPropertiesForPromoCodes();
  const { t } = useTranslation();

  const columnHelper = createColumnHelper<PromoCode>();
  const columns = [
    columnHelper.accessor('code', {
      header: t('common.name'),
      sortDescFirst: true,
    }),
    columnHelper.accessor('value', {
      header: `${t('common.amount')} / ${t('common.percent')}`,
      cell: AmountPercentageCell,
    }),
    columnHelper.accessor('status', {
      header: t('common.status'),
      enableSorting: false,
    }),
    columnHelper.accessor('expirationUTCDateTime', {
      header: t('pageDiscountCodes.pageDiscountCodes.expiresOn'),
      cell: FormattedDateCell,
      sortDescFirst: true,
    }),
    columnHelper.accessor('validFromUTCDateTime', {
      header: t('pageDiscountCodes.pageDiscountCodes.validDates'),
      cell: ValidDatesCell,
      sortDescFirst: true,
    }),
    columnHelper.accessor('propertyUid', {
      header: t('common.property'),
      cell: PropertyNameCell,
      sortDescFirst: true,
      enableSorting: false,
    }),
    columnHelper.accessor('singleUsage', {
      header: t('pageDiscountCodes.pageDiscountCodes.singleUse'),
      cell: YesOrNoCell,
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: ActionsCell,
      meta: {
        isHidden: status === PromoCodeStatus.DELETED,
        showCellTooltip: false,
      },
    }),
  ];

  const { tableInstance } = useServerFilteredTable({
    columns,
    pageSize: 10,
    getQuery: (args) =>
      promoCodesQuery({
        ...args,
        status,
      }),
  });

  return (
    <PromoCodesTableStyled
      data-testid="promo-codes-table"
      emptyState={t('pageDiscountCodes.pageDiscountCodes.noDiscountsFound')}
      tableInstance={tableInstance}
      isLoading={isPropertiesLoading}
      striped
    />
  );
};

export default PromoCodeTable;
